/**
 * Presentational helpers for the Messages page and the contact timeline.
 *
 * Timeline days and times are India time (`Asia/Kolkata`), the day boundary
 * the follow-up rules use, so a separator never disagrees with "due today".
 * The two datetime-local helpers at the bottom read and write the browser's
 * local time, because that is what the input shows.
 */

export const INDIA_TIME_ZONE = "Asia/Kolkata";

// en-CA formats a date as YYYY-MM-DD, which makes a sortable, comparable key.
const indiaDayFormat = new Intl.DateTimeFormat("en-CA", {
	timeZone: INDIA_TIME_ZONE,
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
});

/** The India calendar date of a timestamp, e.g. "2026-10-12". */
export function dayKey(iso: string | Date): string {
	return indiaDayFormat.format(new Date(iso));
}

/** "Today", "Yesterday", or a long India date, for a timeline separator. */
export function dayLabel(
	iso: string,
	locale: string,
	words: { today: string; yesterday: string },
	now: Date = new Date(),
): string {
	const key = dayKey(iso);
	if (key === dayKey(now)) return words.today;
	// Noon India time on the India date of `now`, minus a day: never lands on
	// the wrong date, whatever the browser's own zone is.
	const yesterday = new Date(`${dayKey(now)}T12:00:00+05:30`);
	yesterday.setTime(yesterday.getTime() - 24 * 60 * 60 * 1000);
	if (key === dayKey(yesterday)) return words.yesterday;
	return new Date(iso).toLocaleDateString(locale, {
		timeZone: INDIA_TIME_ZONE,
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

/** India clock time, hour and minute, in `locale`. */
export function clockTime(iso: string, locale: string): string {
	return new Date(iso).toLocaleTimeString(locale, {
		timeZone: INDIA_TIME_ZONE,
		hour: "numeric",
		minute: "2-digit",
	});
}

/** India date and time with a short month name, in `locale`. */
export function dateTime(iso: string, locale: string): string {
	return new Date(iso).toLocaleString(locale, {
		timeZone: INDIA_TIME_ZONE,
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

/** `date` as a datetime-local input value in local time, e.g. "2026-09-20T09:15". */
export function toLocalInputValue(date: Date): string {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Reads a datetime-local value as local time; null when empty or malformed. */
export function parseLocalInput(value: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
	if (!match) return null;
	const [, y, mo, d, h, mi] = match.map(Number);
	const date = new Date(y, mo - 1, d, h, mi);
	return Number.isNaN(date.getTime()) ? null : date;
}
